import type {
  AdminDashboard,
  Availability,
  MatchEvent,
  MatchFound,
  MatchRequestResponse,
  MatchRequestStatus,
  MeResponse,
  Mode,
  RoomPreview,
  RoomParticipant,
  RoomState,
  RatingValue,
  BannedTopic,
  AdminRoomSummary,
  ReportedUserSummary,
  ReportEvidencePayload,
  ReportStatus,
  ReportSummary,
  TopicSuggestion,
  UserModerationBlock,
	HistoryItem,
	ContactRequestSummary,
	ContactSummary,
	UserNotification,
	DirectCallSummary,
	DirectCallResponse,
} from "./types"
import {
  bridgeApiBasePath,
  bridgeAuthorizationHeader,
  bridgeSessionHandle,
  currentBridgeRuntime,
  ensureBridgeRuntime,
  isBridgeEmbeddedCandidate,
  notifyBridgeAuthExpired,
} from "./bridge"
import { authAuthorizationHeader, clearAuthSession, currentAuthSession, refreshAuthSession, storeAuthSession } from "./auth"
import type { AuthSession } from "./auth"

const apiBase = import.meta.env.VITE_API_BASE || "/api"
const forceMock = import.meta.env.VITE_API_MOCK === "true"
const mockTopicKey = "joowfak.mockTopicText"
const mockDirectCallKey = "joowfak.mockDirectCall"
const mockAvatarUrl = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Crect width='80' height='80' rx='22' fill='%23e85c3b'/%3E%3Ctext x='40' y='50' text-anchor='middle' font-size='30' font-family='Arial' font-weight='700' fill='white'%3EJ%3C/text%3E%3C/svg%3E"

export class ApiError extends Error {
  status: number
  code: string

  constructor(message: string, status: number, code = "") {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = code
  }
}

export async function createMatchRequest(input: { topicText?: string; mode: Mode }): Promise<MatchRequestResponse> {
  if (forceMock) return mockCreate(input)
  const response = await apiFetch("/match-requests", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  })
  if (!response.ok) throw await responseError(response)
  return response.json()
}

export async function getAvailability(): Promise<Availability> {
  if (forceMock) return { voiceWaiting: 23, videoWaiting: 7, activeSessions: 46 }
  return getJson<Availability>(`${apiBase}/availability`)
}

export async function getMe(): Promise<MeResponse> {
  if (forceMock) {
    if (sessionStorage.getItem("joowfak.mockSignedOut") === "true") {
      throw new ApiError("missing bearer token", 401, "missing_bearer")
    }
    return {
      appId: "joowfak",
      subjectId: "mock-user",
      displayName: "سحر",
      avatarUrl: mockAvatarUrl,
      avatarPreset: sessionStorage.getItem("joowfak.mockAvatarPreset") || "",
      publicCode: "JFK-MOCKUSER",
      profileComplete: true,
      isAdmin: sessionStorage.getItem("joowfak.mockAdmin") === "true",
      permissions: ["joowfak.access", "joowfak.call.start", "joowfak.report.create"],
    }
  }
  return getJson<MeResponse>(`${apiBase}/me`)
}

export async function updateMyProfile(input: { displayName: string; avatarPreset?: string }): Promise<AuthSession> {
  if (forceMock) {
    const existing = currentAuthSession()
    const session: AuthSession = existing || {
      accessToken: "mock-access-token",
      accessExpiresAt: new Date(Date.now() + 3600_000).toISOString(),
      refreshToken: "mock-refresh-token",
      refreshExpiresAt: new Date(Date.now() + 86_400_000).toISOString(),
      user: { id: "mock-user" },
    }
    session.user = {
      ...session.user,
      id: session.user.id || "mock-user",
      displayName: input.displayName,
      avatarPreset: input.avatarPreset || session.user.avatarPreset || "",
    }
    if (input.avatarPreset) sessionStorage.setItem("joowfak.mockAvatarPreset", input.avatarPreset)
    storeAuthSession(session)
    return session
  }
  const session = await patchJsonResponse<AuthSession>(`${apiBase}/me`, input)
  storeAuthSession(session)
  return session
}

export async function getHistory(): Promise<HistoryItem[]> {
	if (forceMock) {
		return [{
			sessionId: "mock-history", topicLabel: "Iranian football", mode: "video",
			joinedAt: new Date(Date.now() - 3_600_000).toISOString(),
			participants: [{ participantId: "mock-peer", displayName: "Darya", publicCode: "JFK-8C8F", avatarPreset: "face-17" }],
		}]
	}
	const payload = await getJson<{ items: HistoryItem[] }>(`${apiBase}/history`)
	return payload.items
}

export async function getNotifications(): Promise<{ items: UserNotification[]; unreadCount: number }> {
	if (forceMock) return { items: [], unreadCount: 0 }
	return getJson<{ items: UserNotification[]; unreadCount: number }>(`${apiBase}/notifications`)
}

export async function readNotification(notificationId: string): Promise<void> {
	if (forceMock) return
	await postJson(`${apiBase}/notifications/${encodeURIComponent(notificationId)}/read`, {})
}

export async function readAllNotifications(): Promise<void> {
	if (forceMock) return
	await postJson(`${apiBase}/notifications/read-all`, {})
}

export async function getContacts(): Promise<ContactSummary[]> {
	if (forceMock) return []
	const payload = await getJson<{ items: ContactSummary[] }>(`${apiBase}/contacts`)
	return payload.items
}

export async function getContactRequests(): Promise<ContactRequestSummary[]> {
	if (forceMock) return []
	const payload = await getJson<{ items: ContactRequestSummary[] }>(`${apiBase}/contact-requests`)
	return payload.items
}

export async function requestContact(sessionId: string, participantId: string): Promise<void> {
	if (forceMock) return
	await postJson(`${apiBase}/contact-requests`, { sessionId, participantId })
}

export async function respondToContactRequest(requestId: string, action: "accept" | "decline" | "cancel"): Promise<void> {
	if (forceMock) return
	await postJson(`${apiBase}/contact-requests/${encodeURIComponent(requestId)}/${action}`, {})
}

export async function removeContact(contactId: string): Promise<void> {
	if (forceMock) return
	const response = await apiFetch(`${apiBase}/contacts/${encodeURIComponent(contactId)}`, { method: "DELETE" })
	if (!response.ok) throw await responseError(response)
}

export async function registerDevice(input: { token: string; kind: "standard" | "voip"; environment?: "development" | "production"; deviceName?: string }): Promise<{ pushConfigured: boolean }> {
	if (forceMock) return { pushConfigured: false }
	return postJsonResponse<{ ok: true; pushConfigured: boolean }>(`${apiBase}/devices`, { platform: "ios", ...input })
}

export async function unregisterDevice(token: string, kind: "standard" | "voip"): Promise<void> {
	if (forceMock) return
	await postJson(`${apiBase}/devices/unregister`, { token, kind })
}

export async function getDirectCalls(): Promise<DirectCallSummary[]> {
	if (forceMock) return []
	const payload = await getJson<{ items: DirectCallSummary[] }>(`${apiBase}/calls`)
	return payload.items
}

export async function getDirectCall(callId: string): Promise<DirectCallResponse> {
	if (forceMock) return mockGetDirectCall(callId)
	return getJson<DirectCallResponse>(`${apiBase}/calls/${encodeURIComponent(callId)}`)
}

export async function startDirectCall(contactId: string, mode: Mode): Promise<DirectCallResponse> {
	if (forceMock) throw new ApiError("Direct calls require the live backend", 503)
	return postJsonResponse<DirectCallResponse>(`${apiBase}/contacts/${encodeURIComponent(contactId)}/calls`, { mode })
}

export async function transitionDirectCall(callId: string, action: "accept" | "decline" | "cancel" | "connected" | "end"): Promise<DirectCallResponse> {
	if (forceMock) return mockTransitionDirectCall(callId, action)
	return postJsonResponse<DirectCallResponse>(`${apiBase}/calls/${encodeURIComponent(callId)}/${action}`, {})
}

export async function getMyReports(): Promise<ReportSummary[]> {
  if (forceMock) return mockReports("mine")
  const payload = await getJson<{ reports: ReportSummary[] }>(`${apiBase}/reports/mine`)
  return payload.reports
}

export async function requestUnblockClaim(): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/moderation/unblock-claim`, { claim: "blocked user requested unblock review" })
}

export async function getAdminDashboard(): Promise<AdminDashboard> {
  if (forceMock) return mockAdminDashboard()
  return getJson<AdminDashboard>(`${apiBase}/admin/dashboard`)
}

export async function getAdminRooms(): Promise<AdminRoomSummary[]> {
  if (forceMock) return mockAdminRooms()
  const payload = await getJson<{ rooms: AdminRoomSummary[] }>(`${apiBase}/admin/rooms`)
  return payload.rooms
}

export async function closeAdminRoom(sessionId: string): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/admin/rooms/${encodeURIComponent(sessionId)}/close`, {})
}

export async function getAdminReports(status: ReportStatus | "all" = "pending", search = ""): Promise<ReportSummary[]> {
  if (forceMock) return mockReports(status)
  const query = new URLSearchParams({ status })
  if (search.trim()) query.set("q", search.trim())
  const payload = await getJson<{ reports: ReportSummary[] }>(`${apiBase}/admin/reports?${query.toString()}`)
  return payload.reports
}

export async function getAdminReport(reportId: string): Promise<ReportSummary> {
  if (forceMock) {
    const report = mockReports("all").find((item) => item.id === reportId) || mockReports("all")[0]
    return report
  }
  const payload = await getJson<{ report: ReportSummary }>(`${apiBase}/admin/reports/${encodeURIComponent(reportId)}`)
  return payload.report
}

export async function reviewAdminReport(reportId: string, status: ReportStatus): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/admin/reports/${encodeURIComponent(reportId)}/review`, { status })
}

export async function getAdminReportedUsers(range = "7d", minReporters = 10): Promise<{ reportedUsers: ReportedUserSummary[]; blockedUsers: UserModerationBlock[] }> {
  if (forceMock) return mockReportedUsers()
  const query = new URLSearchParams({ range, minReporters: String(minReporters) })
  return getJson<{ reportedUsers: ReportedUserSummary[]; blockedUsers: UserModerationBlock[] }>(`${apiBase}/admin/reported-users?${query.toString()}`)
}

export async function blockAdminReportedUser(subjectKey: string, reason = ""): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/admin/reported-users/${encodeURIComponent(subjectKey)}/block`, { reason })
}

export async function unblockAdminReportedUser(subjectKey: string): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/admin/reported-users/${encodeURIComponent(subjectKey)}/unblock`, {})
}

export async function getAdminTopicBans(): Promise<BannedTopic[]> {
  if (forceMock) return mockTopicBans()
  const payload = await getJson<{ bannedTopics: BannedTopic[] }>(`${apiBase}/admin/topics/moderation`)
  return payload.bannedTopics
}

export async function getAdminTopicSuggestions(queryText: string): Promise<TopicSuggestion[]> {
  const query = queryText.trim()
  if (query.length < 3) return []
  if (forceMock) return mockTopicSuggestions(query)
  const params = new URLSearchParams({ q: query })
  const payload = await getJson<{ topics: TopicSuggestion[] }>(`${apiBase}/admin/topics/suggestions?${params.toString()}`)
  return payload.topics
}

export async function getTopicSuggestions(queryText = ""): Promise<TopicSuggestion[]> {
  const query = queryText.trim()
  if (forceMock) return mockTopicSuggestions(query || "top")
  const params = new URLSearchParams()
  if (query) params.set("q", query)
  const payload = await getJson<{ topics: TopicSuggestion[] }>(`${apiBase}/topics/suggestions${params.toString() ? `?${params.toString()}` : ""}`)
  return payload.topics
}

export async function banAdminTopic(topicText: string, reason = ""): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/admin/topics/ban`, { topicText, reason })
}

export async function unbanAdminTopic(normalizedTopic: string): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/admin/topics/unban`, { normalizedTopic })
}

export async function getMatchRequestStatus(requestId: string): Promise<MatchRequestStatus> {
  if (forceMock) {
    if (shouldMockRoomPreview()) return { requestId, status: "room_preview", roomPreview: mockRoomPreview() }
    return { requestId, status: "searching" }
  }
  return getJson<MatchRequestStatus>(`${apiBase}/match-requests/${encodeURIComponent(requestId)}`)
}

export async function joinRoomCandidate(requestId: string, candidateId: string): Promise<MatchFound> {
  if (forceMock || requestId.startsWith("mock_")) {
    return mockRoomMatch(candidateId)
  }
  const payload = await postJsonResponse<{ status: "found"; match: MatchFound }>(`${apiBase}/match-requests/${encodeURIComponent(requestId)}/room-candidates/${encodeURIComponent(candidateId)}/join`, {})
  return payload.match
}

export async function skipRoomCandidate(requestId: string, candidateId: string): Promise<{ status: "searching" | "room_preview"; roomPreview?: RoomPreview }> {
  if (forceMock || requestId.startsWith("mock_")) {
    sessionStorage.removeItem("joowfak.mockRoomPreview")
    return { status: "searching" }
  }
  return postJsonResponse<{ status: "searching" | "room_preview"; roomPreview?: RoomPreview }>(`${apiBase}/match-requests/${encodeURIComponent(requestId)}/room-candidates/${encodeURIComponent(candidateId)}/skip`, {})
}

export function subscribeMatchEvents(
  requestId: string,
  onEvent: (event: MatchEvent) => void,
): () => void {
  if (forceMock || requestId.startsWith("mock_")) {
    return mockSubscribe(onEvent)
  }
  const controller = new AbortController()
  let closed = false
  let terminalEventSeen = false
  let reconnectTimer: number | undefined
  let resolveReconnectDelay: (() => void) | undefined
  const emit = (event: MatchEvent) => {
    if (closed) return
    if (event.type === "found" || event.type === "room_preview" || event.type === "error") terminalEventSeen = true
    onEvent(event)
  }
  const delay = (ms: number) => new Promise<void>((resolve) => {
    resolveReconnectDelay = resolve
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = undefined
      resolveReconnectDelay = undefined
      resolve()
    }, ms)
  })
  const emitStatusIfReady = async (): Promise<boolean> => {
    const current = await getMatchRequestStatus(requestId)
    if (current.status === "found" && current.match) {
      emit({ type: "found", match: current.match })
      return true
    }
    if (current.status === "room_preview" && current.roomPreview) {
      emit({ type: "room_preview", roomPreview: current.roomPreview })
      return true
    }
    if (current.status === "cancelled" || current.status === "expired") {
      emit({ type: "error", error: current.status === "expired" ? "match request expired" : "match request cancelled" })
      return true
    }
    return false
  }

  void (async () => {
    let reconnectAttempts = 0
    while (!closed && !terminalEventSeen) {
      try {
        const response = await apiFetch(
          withSessionHandle(apiUrl(`/match-requests/${encodeURIComponent(requestId)}/events`)),
          {
            headers: { Accept: "text/event-stream" },
            signal: controller.signal,
          },
        )
        if (!response.ok) throw await responseError(response)
        if (!response.body) throw new Error("matching stream is unavailable")
        reconnectAttempts = 0
        await readMatchEventStream(response.body, emit, () => closed)
        if (closed || terminalEventSeen) return
        if (await emitStatusIfReady()) return
      } catch (err) {
        if (closed || controller.signal.aborted) return
        if (err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404)) {
          emit({ type: "error", error: err.message })
          return
        }
        try {
          if (await emitStatusIfReady()) return
        } catch {
          // Fall through to reconnect; transient network loss should not end matching.
        }
      }
      reconnectAttempts = Math.min(reconnectAttempts + 1, 5)
      await delay(500 + reconnectAttempts * 500)
    }
  })()

  return () => {
    closed = true
    if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer)
    resolveReconnectDelay?.()
    controller.abort()
  }
}

export async function cancelMatchRequest(requestId: string): Promise<void> {
  if (forceMock || requestId.startsWith("mock_")) return
  await postJson(`${apiBase}/match-requests/${encodeURIComponent(requestId)}/cancel`, {})
}

export function cancelMatchRequestOnPageExit(requestId: string): void {
  if (forceMock || !requestId || requestId.startsWith("mock_")) return
  postJsonOnPageExit(`${apiBase}/match-requests/${encodeURIComponent(requestId)}/cancel`, {})
}

export async function recoverSession(sessionId: string, peerToken: string): Promise<MatchFound> {
  if (forceMock) {
    if (shouldMockPeerLeft()) return mockPeerLeftMatch(sessionId, peerToken)
    return { ...mockMatch(), sessionId, peerToken }
  }
  const query = new URLSearchParams({ peerToken })
  return getJson<MatchFound>(`${apiBase}/sessions/${encodeURIComponent(sessionId)}?${query.toString()}`)
}

export async function recoverSessionWithParticipant(sessionId: string, peerToken = "", participantToken = ""): Promise<MatchFound> {
  if (forceMock) {
    if (shouldMockPeerLeft()) return mockPeerLeftMatch(sessionId, peerToken, participantToken)
    return { ...mockMatch(), sessionId, peerToken, participantToken }
  }
  const query = new URLSearchParams()
  if (peerToken) query.set("peerToken", peerToken)
  if (participantToken) query.set("participantToken", participantToken)
  return getJson<MatchFound>(`${apiBase}/sessions/${encodeURIComponent(sessionId)}?${query.toString()}`)
}

export async function getSessionEvents(sessionId: string, participantToken = ""): Promise<RoomState> {
  if (forceMock) return mockRoomState(sessionId)
  const query = new URLSearchParams()
  if (participantToken) query.set("participantToken", participantToken)
  return getJson<RoomState>(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/events?${query.toString()}`)
}

export async function endSession(sessionId: string): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/end`, {})
}

export function endSessionOnPageExit(sessionId: string): void {
  if (forceMock || !sessionId) return
  postJsonOnPageExit(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/end`, { reason: "browser_close" })
}

export async function leaveSessionParticipant(sessionId: string, participantToken = "", reason = "end"): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/participants/leave`, { participantToken, reason })
}

export function leaveSessionParticipantOnPageExit(sessionId: string, participantToken = ""): void {
  if (forceMock || !sessionId) return
  postJsonOnPageExit(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/participants/leave`, { participantToken, reason: "browser_close" })
}

export async function startOpenSeatVote(sessionId: string, participantToken: string): Promise<RoomState> {
  if (forceMock) return mockRoomState(sessionId, "active")
  return postJsonResponse<RoomState>(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/open-seat-votes`, { participantToken })
}

export async function ballotOpenSeatVote(sessionId: string, voteId: string, participantToken: string, value: "yes" | "no" | "cancel"): Promise<RoomState> {
  if (forceMock) return mockRoomState(sessionId, value === "cancel" ? "active" : "active")
  return postJsonResponse<RoomState>(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/open-seat-votes/${encodeURIComponent(voteId)}/ballot`, { participantToken, value })
}

export async function nextSession(sessionId: string): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/next`, {})
}

export async function rateSession(sessionId: string, value: RatingValue, report = false, block = false): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/rating`, { value, report, block })
}

export async function reportSession(sessionId: string, peerToken: string, targetParticipantId = "", reason = "safety", notes = "", block = false, evidenceConsent = false, evidence?: ReportEvidencePayload | null): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/report`, { peerToken, targetParticipantId, reason, notes, block, evidenceConsent, evidence })
}

export async function blockSession(sessionId: string, peerToken: string): Promise<void> {
  if (forceMock) return
  await postJson(`${apiBase}/sessions/${encodeURIComponent(sessionId)}/block`, { peerToken })
}

async function getJson<T>(url: string): Promise<T> {
  const response = await apiFetch(url)
  if (!response.ok) throw await responseError(response)
  return response.json()
}

async function postJson(url: string, body: unknown): Promise<void> {
  const response = await apiFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
  if (!response.ok) throw await responseError(response)
}

async function postJsonResponse<T>(url: string, body: unknown): Promise<T> {
  const response = await apiFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
  if (!response.ok) throw await responseError(response)
  return response.json()
}

async function patchJsonResponse<T>(url: string, body: unknown): Promise<T> {
  const response = await apiFetch(url, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
  if (!response.ok) throw await responseError(response)
  return response.json()
}

function postJsonOnPageExit(url: string, body: unknown): void {
  const payload = JSON.stringify(body)
  const target = withSessionHandle(apiUrl(url))
  let queued = false
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      queued = navigator.sendBeacon(target, new Blob([payload], { type: "application/json" }))
    } catch {
      queued = false
    }
  }
  if (queued) return
  try {
    void fetch(target, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "same-origin",
    })
  } catch {
    // The page is going away; there is no user-visible recovery path here.
  }
}

async function apiFetch(urlOrPath: string, init: RequestInit = {}): Promise<Response> {
  if (isBridgeEmbeddedCandidate() && !await ensureBridgeRuntime()) {
    return new Response(JSON.stringify({
      code: "joow_shell_session_required",
      error: "JooW shell session is required",
    }), {
      status: 401,
      headers: { "Content-Type": "application/json" },
    })
  }
  const headers = new Headers(init.headers)
  const authorization = bridgeAuthorizationHeader() || authAuthorizationHeader()
  if (authorization && !headers.has("Authorization")) headers.set("Authorization", authorization)
  let response = await fetch(apiUrl(urlOrPath), {
    ...init,
    credentials: init.credentials || "same-origin",
    headers,
  })
  if (response.status === 401 && !bridgeAuthorizationHeader() && authAuthorizationHeader()) {
    const refreshed = await refreshAuthSession()
    if (refreshed) {
      headers.set("Authorization", `Bearer ${refreshed.accessToken}`)
      response = await fetch(apiUrl(urlOrPath), {
        ...init,
        credentials: init.credentials || "same-origin",
        headers,
      })
    }
  }
  if (response.status === 401) {
    notifyBridgeAuthExpired()
    if (!bridgeAuthorizationHeader()) clearAuthSession()
  }
  return response
}

function apiUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  const base = bridgeApiBasePath() || apiBase
  if (pathOrUrl.startsWith(base)) return pathOrUrl
  if (pathOrUrl.startsWith(apiBase)) return joinUrl(base, pathOrUrl.slice(apiBase.length) || "/")
  return joinUrl(base, pathOrUrl)
}

function joinUrl(base: string, path: string): string {
  const cleanBase = base.replace(/\/$/, "")
  const cleanPath = path.startsWith("/") ? path : `/${path}`
  return `${cleanBase}${cleanPath}`
}

function withSessionHandle(url: string): string {
  const handle = bridgeSessionHandle()
  if (!handle || !currentBridgeRuntime()) return url
  const parsed = new URL(url, window.location.origin)
  if (!parsed.searchParams.has("joow_app_session_handle")) {
    parsed.searchParams.set("joow_app_session_handle", handle)
  }
  if (url.startsWith("http")) return parsed.toString()
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

async function readMatchEventStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: MatchEvent) => void,
  isClosed: () => boolean,
): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  try {
    while (!isClosed()) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      buffer = drainMatchEventBuffer(buffer, onEvent)
    }
    buffer += decoder.decode()
    drainMatchEventBuffer(buffer, onEvent)
  } finally {
    reader.releaseLock()
  }
}

function drainMatchEventBuffer(buffer: string, onEvent: (event: MatchEvent) => void): string {
  let normalized = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
  let boundary = normalized.indexOf("\n\n")
  while (boundary >= 0) {
    const frame = normalized.slice(0, boundary)
    normalized = normalized.slice(boundary + 2)
    const event = parseMatchEventFrame(frame)
    if (event) onEvent(event)
    boundary = normalized.indexOf("\n\n")
  }
  return normalized
}

function parseMatchEventFrame(frame: string): MatchEvent | null {
  let eventType = ""
  const data: string[] = []
  for (const rawLine of frame.split("\n")) {
    if (!rawLine || rawLine.startsWith(":")) continue
    const separator = rawLine.indexOf(":")
    const field = separator >= 0 ? rawLine.slice(0, separator) : rawLine
    let value = separator >= 0 ? rawLine.slice(separator + 1) : ""
    if (value.startsWith(" ")) value = value.slice(1)
    if (field === "event") eventType = value
    if (field === "data") data.push(value)
  }
  if (!data.length) {
    return isMatchEventType(eventType) ? { type: eventType } : null
  }
  try {
    const parsed = JSON.parse(data.join("\n")) as Partial<MatchEvent>
    if (isMatchEventType(parsed.type)) return parsed as MatchEvent
    if (isMatchEventType(eventType)) return { ...parsed, type: eventType } as MatchEvent
  } catch {
    return { type: "error", error: "matching stream payload was invalid" }
  }
  return null
}

function isMatchEventType(value: unknown): value is MatchEvent["type"] {
  return value === "searching" || value === "found" || value === "room_preview" || value === "error" || value === "ping"
}

async function responseError(response: Response): Promise<ApiError> {
  const body = await response.text()
  if (!body.trim()) return new ApiError(response.statusText || `HTTP ${response.status}`, response.status)
  try {
    const parsed = JSON.parse(body) as { code?: unknown; error?: unknown; message?: unknown }
    const code = typeof parsed.code === "string" ? parsed.code : ""
    const message = typeof parsed.error === "string" ? parsed.error : typeof parsed.message === "string" ? parsed.message : ""
    return new ApiError(message || body, response.status, code)
  } catch {
    return new ApiError(body, response.status)
  }
}

function mockCreate(input: { topicText?: string }): MatchRequestResponse {
  const topicText = input.topicText?.trim() || ""
  if (!topicText) throw new ApiError("topic is required", 400, "topic_required")
  sessionStorage.setItem(mockTopicKey, topicText)
  const requestId = `mock_${crypto.randomUUID()}`
  if (shouldMockRoomPreview()) return { requestId, status: "room_preview", roomPreview: mockRoomPreview() }
  return { requestId, status: "searching" }
}

function mockSubscribe(onEvent: (event: MatchEvent) => void): () => void {
  onEvent({ type: "searching" })
  const timeout = window.setTimeout(() => {
    if (shouldMockRoomPreview()) onEvent({ type: "room_preview", roomPreview: mockRoomPreview() })
    else onEvent({ type: "found", match: mockMatch() })
  }, mockMatchDelayMs())
  return () => window.clearTimeout(timeout)
}

function mockMatchDelayMs(): number {
  const configured = Number(sessionStorage.getItem("joowfak.mockMatchDelayMs"))
  if (Number.isFinite(configured) && configured >= 0) return configured
  return 850
}

function mockMatch(): MatchFound {
  const topicText = sessionStorage.getItem(mockTopicKey) || sessionStorage.getItem("joowfak.topic") || ""
  return {
    sessionId: `sess_${crypto.randomUUID()}`,
    peerToken: `peer_${crypto.randomUUID()}`,
    participantToken: `participant_${crypto.randomUUID()}`,
    selfParticipantId: "part_mock_self",
    topicLabel: topicText || "فوتبال ایران",
    attendeeCount: 2,
    participants: [
      { id: "part_mock_self", code: "#P8A22F", displayName: "You", publicCode: "JFK-MOCKYOU", topicLabel: topicText || "فوتبال ایران", topicKeywords: topicText || "فوتبال ایران", matchScore: 0.91, active: true, joinedAt: new Date().toISOString() },
      { id: "part_mock_peer", code: "#P4C91B", displayName: "Darya", publicCode: "JFK-DARYA42", topicLabel: "دربی تهران", topicKeywords: "دربی تهران", matchScore: 0.87, active: true, joinedAt: new Date().toISOString() },
    ],
    liveness: { remainingSeconds: 0, momentum: "steady" },
    matchScore: topicText ? 0.91 : undefined,
    signaling: { url: "/mock-signaling", ticket: "mock-ticket" },
    media: { iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }] },
  }
}

function shouldMockPeerLeft(): boolean {
  return sessionStorage.getItem("joowfak.mockPeerLeft") === "true"
}

function mockPeerLeftParticipants(): RoomParticipant[] {
  return [
    { id: "part_mock_self", code: "#P8A22F", displayName: "You", publicCode: "JFK-MOCKYOU", active: true, joinedAt: new Date(Date.now() - 120000).toISOString() },
    { id: "part_mock_peer", code: "#P4C91B", displayName: "Darya", publicCode: "JFK-DARYA42", active: false, joinedAt: new Date(Date.now() - 110000).toISOString(), leftAt: new Date(Date.now() - 5000).toISOString() },
  ]
}

function mockPeerLeftMatch(sessionId?: string, peerToken?: string, participantToken?: string): MatchFound {
  const match = mockMatch()
  return {
    ...match,
    sessionId: sessionId || match.sessionId,
    peerToken: peerToken || match.peerToken,
    participantToken: participantToken || match.participantToken,
    attendeeCount: 1,
    participants: mockPeerLeftParticipants(),
  }
}

function mockRoomMatch(candidateId: string): MatchFound {
  const match = mockMatch()
  return {
    ...match,
    sessionId: candidateId || match.sessionId,
    peerToken: "",
    attendeeCount: 3,
    participants: [
      ...(match.participants || []),
      { id: "part_mock_third", code: "#P9FF20", displayName: "Omid", publicCode: "JFK-OMID777", active: true, joinedAt: new Date().toISOString() },
    ],
    liveness: { remainingSeconds: 3600, momentum: "up" },
  }
}

function shouldMockRoomPreview(): boolean {
  return sessionStorage.getItem("joowfak.mockRoomPreview") === "true"
}

function mockRoomPreview(): RoomPreview {
  const topicText = sessionStorage.getItem(mockTopicKey) || sessionStorage.getItem("joowfak.topic") || "فلسفه‌بافی"
  return {
    candidateId: "sess_room_preview",
    sessionId: "sess_room_preview",
    topicLabel: topicText,
    attendeeCount: 3,
    livenessRemainingSeconds: 2700,
    momentum: "up",
    matchScore: 0.94,
    mode: "voice",
  }
}

function mockRoomState(sessionId: string, voteStatus: "active" | "approved" | "" = ""): RoomState {
  if (shouldMockPeerLeft() && !voteStatus) {
    return {
      sessionId,
      attendeeCount: 1,
      participants: mockPeerLeftParticipants(),
      liveness: { remainingSeconds: 0, momentum: "down" },
    }
  }
  const vote = voteStatus ? {
    id: "vote_mock",
    status: voteStatus,
    eligibleVoterCount: 2,
    eligibleParticipantIds: ["part_mock_self", "part_mock_peer"],
    yesVotes: voteStatus === "approved" ? 2 : 1,
    noVotes: 0,
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 45000).toISOString(),
    seatExpiresAt: voteStatus === "approved" ? new Date(Date.now() + 120000).toISOString() : null,
  } : undefined
  return {
    sessionId,
    attendeeCount: 2,
    participants: [
      { id: "part_mock_self", code: "#P8A22F", displayName: "You", publicCode: "JFK-MOCKYOU", avatarPreset: "face-02", topicLabel: "فوتبال ایران", topicKeywords: "فوتبال ایران", matchScore: 0.91, active: true, joinedAt: new Date().toISOString() },
      { id: "part_mock_peer", code: "#P4C91B", displayName: "Darya", publicCode: "JFK-DARYA42", avatarPreset: "face-09", topicLabel: "دربی تهران", topicKeywords: "دربی تهران", matchScore: 0.87, active: true, joinedAt: new Date().toISOString() },
    ],
    liveness: { remainingSeconds: 1800, momentum: "steady" },
    vote,
  }
}

function mockAdminDashboard(): AdminDashboard {
  return {
    metrics: {
      onlineNow: 2314,
      activeSessions: 487,
      devActiveSessions: 0,
      averageDurationSeconds: 252,
      matchRate: 0.94,
      reportsToday: 23,
      pendingReports: 23,
    },
    liveSessions: [
      { conversationCode: "#F7A2", topicLabel: "فوتبال ایران", mode: "voice", matchScore: 0.91, createdAt: new Date(Date.now() - 130000).toISOString(), endReason: "" },
      { conversationCode: "#M2P9", topicLabel: "فلسفه‌بافی", mode: "video", matchScore: 0.86, createdAt: new Date(Date.now() - 460000).toISOString(), endReason: "" },
      { conversationCode: "#K8D4", topicLabel: "مهاجرت", mode: "voice", matchScore: 0.83, createdAt: new Date(Date.now() - 790000).toISOString(), endReason: "" },
    ],
    reportQueue: mockReports("pending"),
    hotTopics: [
      { label: "فوتبال ایران", count: 840 },
      { label: "فلسفه‌بافی", count: 612 },
      { label: "مهاجرت", count: 485 },
      { label: "موسیقی", count: 420 },
    ],
  }
}

function mockAdminRooms(): AdminRoomSummary[] {
  return [
    {
      conversationCode: "#R0M1",
      sessionId: "sess_room_1",
      topicLabel: "فلسفه‌بافی",
      topicKeywords: "فلسفه‌بافی",
      mode: "voice",
      matchScore: 0.91,
      attendeeCount: 3,
      participants: [
        { id: "part_a", code: "#P8A22F", topicLabel: "فلسفه‌بافی", topicKeywords: "فلسفه‌بافی", matchScore: 0.95, active: true, joinedAt: new Date(Date.now() - 900000).toISOString() },
        { id: "part_b", code: "#P4C91B", topicLabel: "اخلاق", topicKeywords: "اخلاق", matchScore: 0.89, active: true, joinedAt: new Date(Date.now() - 860000).toISOString() },
        { id: "part_c", code: "#P9FF20", topicLabel: "تاریخ فلسفه", topicKeywords: "تاریخ فلسفه", matchScore: 0.83, active: true, joinedAt: new Date(Date.now() - 120000).toISOString() },
      ],
      activeVote: {
        id: "vote_room_1",
        status: "active",
        eligibleVoterCount: 3,
        eligibleParticipantIds: ["part_a", "part_b", "part_c"],
        yesVotes: 2,
        noVotes: 0,
        createdAt: new Date(Date.now() - 10000).toISOString(),
        expiresAt: new Date(Date.now() + 35000).toISOString(),
      },
      openSeatExpiresAt: null,
      liveness: { remainingSeconds: 3600, momentum: "up", capSeconds: 86400 },
      createdAt: new Date(Date.now() - 900000).toISOString(),
    },
    {
      conversationCode: "#S0L1",
      sessionId: "sess_room_solo",
      topicLabel: "فوتبال ایران",
      topicKeywords: "فوتبال ایران",
      mode: "voice",
      matchScore: 0.88,
      attendeeCount: 1,
      participants: [
        { id: "part_solo_a", code: "#P0A11A", active: true, joinedAt: new Date(Date.now() - 420000).toISOString() },
        { id: "part_solo_b", code: "#P0B22B", active: false, joinedAt: new Date(Date.now() - 400000).toISOString(), leftAt: new Date(Date.now() - 45000).toISOString() },
      ],
      activeVote: null,
      openSeatExpiresAt: null,
      liveness: { remainingSeconds: 2400, momentum: "steady", capSeconds: 86400 },
      createdAt: new Date(Date.now() - 420000).toISOString(),
    },
    {
      conversationCode: "#ST4L",
      sessionId: "sess_room_stale",
      topicLabel: "مهاجرت",
      topicKeywords: "مهاجرت",
      mode: "voice",
      matchScore: 0.8,
      attendeeCount: 1,
      participants: [
        { id: "part_stale_a", code: "#P7A33D", topicLabel: "مهاجرت", topicKeywords: "مهاجرت", matchScore: 0.8, active: true, joinedAt: new Date(Date.now() - 4000000).toISOString() },
      ],
      activeVote: null,
      openSeatExpiresAt: null,
      liveness: { remainingSeconds: 0, momentum: "down", capSeconds: 86400 },
      createdAt: new Date(Date.now() - 4000000).toISOString(),
      isStale: true,
    },
  ]
}

function mockReports(status: ReportStatus | "all" | "mine"): ReportSummary[] {
  const reports: ReportSummary[] = [
    {
      id: "report_mock_1",
      conversationCode: "#Q4R8",
      topicLabel: "فوتبال ایران",
      mode: "voice",
      reason: "harassment",
      notes: "",
      blocked: true,
      targetDisplayName: "Darya",
      targetPublicCode: "JFK-DARYA42",
      status: "pending",
      createdAt: new Date(Date.now() - 120000).toISOString(),
    },
    {
      id: "report_mock_2",
      conversationCode: "#X8L2",
      topicLabel: "موسیقی زیرزمینی",
      mode: "video",
      reason: "sexual",
      notes: "",
      blocked: false,
      targetDisplayName: "Omid",
      targetPublicCode: "JFK-OMID777",
      status: "pending",
      createdAt: new Date(Date.now() - 480000).toISOString(),
    },
    {
      id: "report_mock_3",
      conversationCode: "#L5N1",
      topicLabel: "استارتاپ",
      mode: "voice",
      reason: "scam",
      notes: "",
      blocked: true,
      targetDisplayName: "Nika",
      targetPublicCode: "JFK-NIKA900",
      status: "valid",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      reviewedAt: new Date(Date.now() - 82000000).toISOString(),
    },
    {
      id: "report_mock_4",
      conversationCode: "#A1B2",
      topicLabel: "سینمای ایران",
      mode: "voice",
      reason: "other",
      notes: "",
      blocked: false,
      targetDisplayName: "Arman",
      targetPublicCode: "JFK-ARMAN55",
      status: "invalid",
      createdAt: new Date(Date.now() - 604800000).toISOString(),
      reviewedAt: new Date(Date.now() - 596000000).toISOString(),
    },
  ]
  if (status === "all" || status === "mine") return reports
  return reports.filter((report) => report.status === status)
}

function mockReportedUsers(): { reportedUsers: ReportedUserSummary[]; blockedUsers: UserModerationBlock[] } {
  const now = new Date()
  return {
    reportedUsers: [
      {
        subjectKey: "MOCKSUBJECT000001",
        subjectCode: "#U8A22F0",
        reportCount: 14,
        reporterCount: 11,
        sessionCount: 9,
        lastReportedAt: new Date(now.getTime() - 7200000).toISOString(),
        blocked: false,
      },
      {
        subjectKey: "MOCKSUBJECT000002",
        subjectCode: "#U2C91BB",
        reportCount: 19,
        reporterCount: 13,
        sessionCount: 12,
        lastReportedAt: new Date(now.getTime() - 2600000).toISOString(),
        blocked: true,
      },
    ],
    blockedUsers: [
      {
        subjectKey: "MOCKSUBJECT000002",
        subjectCode: "#U2C91BB",
        reason: "repeat reports",
        enabled: true,
        unblockClaim: "blocked user requested unblock review",
        unblockClaimStatus: "pending",
        unblockClaimedAt: new Date(now.getTime() - 900000).toISOString(),
        createdAt: new Date(now.getTime() - 3600000).toISOString(),
        updatedAt: new Date(now.getTime() - 3600000).toISOString(),
      },
    ],
  }
}

function mockTopicBans(): BannedTopic[] {
  return [
    {
      normalizedTopic: "دعوای شخصی",
      label: "دعوای شخصی",
      reason: "unsafe topic",
      enabled: true,
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
    },
  ]
}

interface MockDirectCallFixture extends DirectCallResponse {
  pollCount?: number
  autoAcceptAfterPolls?: number
  rotateSignalingTicket?: boolean
  baseSignalingTicket?: string
}

function readMockDirectCall(callId: string): MockDirectCallFixture {
  const raw = sessionStorage.getItem(mockDirectCallKey)
  if (!raw) throw new ApiError("Mock call not found", 404)
  const fixture = JSON.parse(raw) as MockDirectCallFixture
  if (fixture.call?.id !== callId) throw new ApiError("Mock call not found", 404)
  return fixture
}

function writeMockDirectCall(fixture: MockDirectCallFixture): void {
  sessionStorage.setItem(mockDirectCallKey, JSON.stringify(fixture))
}

function cloneMockDirectCall(fixture: MockDirectCallFixture): DirectCallResponse {
  return JSON.parse(JSON.stringify({ call: fixture.call, signaling: fixture.signaling })) as DirectCallResponse
}

function mockGetDirectCall(callId: string): DirectCallResponse {
  const fixture = readMockDirectCall(callId)
  fixture.pollCount = (fixture.pollCount || 0) + 1
  if (
    fixture.call.direction === "outgoing" &&
    fixture.call.status === "ringing" &&
    fixture.autoAcceptAfterPolls &&
    fixture.pollCount >= fixture.autoAcceptAfterPolls
  ) {
    fixture.call.status = "accepted"
    fixture.call.answeredAt = new Date().toISOString()
  }
  if (fixture.rotateSignalingTicket && fixture.signaling) {
    fixture.baseSignalingTicket ||= fixture.signaling.ticket
    fixture.signaling.ticket = `${fixture.baseSignalingTicket}.${fixture.pollCount}`
  }
  writeMockDirectCall(fixture)
  return cloneMockDirectCall(fixture)
}

function mockTransitionDirectCall(callId: string, action: "accept" | "decline" | "cancel" | "connected" | "end"): DirectCallResponse {
  const fixture = readMockDirectCall(callId)
  const statusByAction: Record<typeof action, DirectCallSummary["status"]> = {
    accept: "accepted",
    decline: "declined",
    cancel: "cancelled",
    connected: "connected",
    end: "ended",
  }
  fixture.call.status = statusByAction[action]
  if (action === "accept") fixture.call.answeredAt = new Date().toISOString()
  if (["decline", "cancel", "end"].includes(action)) fixture.call.endedAt = new Date().toISOString()
  writeMockDirectCall(fixture)
  return cloneMockDirectCall(fixture)
}

function mockTopicSuggestions(query: string): TopicSuggestion[] {
  const normalized = query.trim().toLowerCase()
  return mockAdminDashboard().hotTopics
    .filter((topic) => topic.label.toLowerCase().includes(normalized))
    .slice(0, 8)
}
